<template>
  <div class="baidu-map-container">
    <div
      v-if="map == null"
      class="loader-container">
      <div class="flex-h">
        <loader-small class="mr-3" />
        <span>{{ $t('messages.initializingMap') }}</span>
      </div>
    </div>
    <baidu-map
      class="map"
      :center="center"
      :zoom="zoom"
      :high-resolution="true"
      :scroll-wheel-zoom="scrollWheelZoom"
      :continuous-zoom="true"
      @ready="onMapReady"
      @click="onMapClicked">
      <bm-map-type
        :map-types="['BMAP_NORMAL_MAP', 'BMAP_HYBRID_MAP']"
        anchor="BMAP_ANCHOR_BOTTOM_RIGHT" />
      <bm-navigation anchor="BMAP_ANCHOR_TOP_RIGHT" />
      <bm-scale anchor="BMAP_ANCHOR_BOTTOM_LEFT" />
    </baidu-map>
  </div>
</template>

<script>
  import turfBbox from '@turf/bbox';
  import turfBboxPolygon from '@turf/bbox-polygon';
  import turfTransformScale from '@turf/transform-scale';
  import mapboxgl from 'mapbox-gl';

  import Colors from '@/Colors';
  import { LoaderSmall } from '@/common/components/controls';
  import { MapInventoryType } from '@/common/enums/map.enums';
  import { debounce, flattenGeoJsonGeometry, getBounds } from '@/globals';
  import { waitUntil } from '@/utils/DelayHelper';
  import measureText from '@/utils/MeasureText';

  export default {
    name: 'BaiduMapWrapper',
    components: {
      LoaderSmall,
    },
    props: {
      polygonFeatures: {
        type: Array,
        default: null,
      },
      centroidFeatures: {
        type: Array,
        default: null,
      },
      geofenceGroupPolygonFeatures: {
        type: Array,
        default: null,
      },
      geofenceGroupCentroidFeatures: {
        type: Array,
        default: null,
      },
      fitBoundsRightPadding: {
        type: Number,
        default: 24,
      },
      geocodedLocationsGeoJson: {
        type: Object,
      },
      units: {
        type: Array,
      },
      marketFilters: {
        type: Array,
      },
      marketPolygonFeatures: {
        type: Array,
      },
      locationFilters: {
        type: Array,
      },
      locationPolygonFeatures: {
        type: Array,
      },
      geofenceGroupFilters: {
        type: Array,
      },
      isReferenceDataReady: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        BMap: null,
        bounds: [],
        zoom: 10,
        center: null,
        map: null,
        numberSizes: {},
        scrollWheelZoom: false,
        proximityMarkers: [],
        radiusCircles: [],
        timeout: null,
        isTriggeredFromCenterAndZoom: false,
      };
    },
    computed: {
      clusters() {
        const clusters = this.geocodedLocationsGeoJson.features.map((feature) => ({
          lng: feature.geometry.coordinates[0],
          lat: feature.geometry.coordinates[1],
          count: feature.properties.count,
          countFormatted: feature.properties.countFormatted,
        }));
        return clusters;
      },
    },
    watch: {
      clusters() {
        if (this.clusterWatchTimer != null) {
          clearTimeout(this.clusterWatchTimer);
        }
        this.clusterWatchTimer = setTimeout(() => {
          this.clusterWatchTimer = null;
          this.updateClusterMarkerOverlays();
        }, 200);
      },
      marketPolygonFeatures() {
        if (this.marketPolygonFeaturesWatchTimer != null) {
          clearTimeout(this.marketPolygonFeaturesWatchTimer);
        }
        this.marketPolygonFeaturesWatchTimer = setTimeout(() => {
          this.marketPolygonFeaturesWatchTimer = null;
          this.updateMarketOverlays();
          this.fitToBoundsOfAllLocations();
        }, 200);
      },
      locationPolygonFeatures() {
        if (this.locationPolygonFeaturesWatchTimer != null) {
          clearTimeout(this.locationPolygonFeaturesWatchTimer);
        }
        this.locationPolygonFeaturesWatchTimer = setTimeout(() => {
          this.locationPolygonFeaturesWatchTimer = null;
          this.updateLocationOverlays();
          this.fitToBoundsOfAllLocations();
        }, 200);
      },
      geofenceGroupFilters() {
        this.updateGeofenceGroupPolygonsDatasource();
      },
      centroidFeatures() {
        this.updateGeofencePolygonsFromProximitySearchDatasource();
      },
    },
    mounted() {
      const chars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', ',', '.', 'k', 'm', 'b', '千'];
      chars.forEach((c) => {
        this.numberSizes[c] = measureText(c, 'bold 15px Oxygen');
      });
    },
    methods: {
      requestLocationsUpdate(fitToBounds = false) {
        this.$emit('requestLocationsUpdate', fitToBounds);
      },
      updateMapDatasourcesAndLayers(fitToBounds = true) {
        if (!this.isMapLoaded || !this.isReferenceDataReady || this.geocodedLocationsGeoJson.length === 0) {
          return;
        }

        //
        // Zoom on locations.
        //
        if (!this.didFitToBoundsOnce || fitToBounds) {
          this.didFitToBoundsOnce = true;
          this.fitToBoundsOfAllLocations();
        }
      },
      clearActiveSiteOrUnitFromMap() {
        // Do nothing
      },
      updateGeofenceGroupPolygonsDatasource() {
        if (this.proximityMarkers.length > 0) {
          this.proximityMarkers.forEach((marker) => {
            this.map.removeOverlay(marker);
          });
        }
        this.geofenceGroupFilters.forEach((geofenceGroupFilter) => {
          geofenceGroupFilter.geofences.forEach((geofence) => {
            const icon = new this.BMap.Icon('/assets/images/map/pin-x1-blue.png', new this.BMap.Size(12, 12), {
              anchor: new this.BMap.Size(6, 6),
              infoWindowAnchor: new this.BMap.Size(6, 0),
            });

            const pointCoords = new this.BMap.Point(geofence.lng, geofence.lat);

            const circle = new this.BMap.Marker(pointCoords, {
              icon,
            });

            this.map.addOverlay(circle);
            this.proximityMarkers.push(circle);

            // Add Radius
            const radiusCircle = new this.BMap.Circle(pointCoords, geofence.radius * 1000, {
              strokeColor: Colors.geofences,
              fillColor: Colors.geofences,
              strokeWeight: 1,
              strokeOpacity: 0.8,
              fillOpacity: 0.2,
              strokeStyle: 'solid',
            });
            this.map.addOverlay(radiusCircle);
            this.radiusCircles.push(radiusCircle);
          });
        });
      },
      async updateGeofencePolygonsFromProximitySearchDatasource() {
        await waitUntil(() => this.map != null);
        if (this.map == null) {
          return;
        }

        this.map.clearOverlays();

        this.centroidFeatures.forEach((feature) => {
          const icon = new this.BMap.Icon('/assets/images/map/pin-x1-blue.png', new this.BMap.Size(12, 12), {
            anchor: new this.BMap.Size(6, 6),
            infoWindowAnchor: new this.BMap.Size(6, 0),
          });
          const point = new this.BMap.Point(feature.geometry.coordinates[0], feature.geometry.coordinates[1]);

          const circle = new this.BMap.Marker(point, {
            icon,
          });
          this.map.addOverlay(circle);
          this.proximityMarkers.push(circle);

          // Add Radius
          const radiusCircle = new this.BMap.Circle(point, feature.properties.radius * 1000, {
            strokeColor: Colors.geofences,
            fillColor: Colors.geofences,
            strokeWeight: 1,
            strokeOpacity: 0.8,
            fillOpacity: 0.2,
            strokeStyle: 'solid',
          });
          this.map.addOverlay(radiusCircle);
          this.radiusCircles.push(radiusCircle);
        });
      },
      getBounds() {
        if (this.bounds.length === 0) {
          return this.bounds;
        }
        const poly = turfBboxPolygon(this.bounds);
        const scaledPoly = turfTransformScale(poly, 1.5);
        const scaledBbox = turfBbox(scaledPoly);
        return scaledBbox;
      },
      getZoom() {
        return Math.max(1, this.zoom - 3);
      },
      getClusterBounds() {
        if (this.geocodedLocationsGeoJson == null || this.geocodedLocationsGeoJson.length === 0) {
          return null;
        }
        return new mapboxgl.LngLatBounds(
          this.geocodedLocationsGeoJson.features.reduce(
            (mem, c) => [
              ...mem,
              [c.properties.bounds.min_lon, c.properties.bounds.min_lat],
              [c.properties.bounds.max_lon, c.properties.bounds.max_lat],
            ],
            [],
          ),
        );
      },
      getUnitsAndSitesBounds() {
        const bounds = getBounds(this.units.map((x) => [x.longitude, x.latitude]));
        if (bounds != null) {
          return new mapboxgl.LngLatBounds(bounds);
        }
        return null;
      },
      getMarketsBounds(list = this.marketFilters) {
        const flattenedMarketCoordinates = flattenGeoJsonGeometry(list.map((x) => x.geometry));
        const bounds = getBounds(flattenedMarketCoordinates);
        if (bounds != null) {
          return new mapboxgl.LngLatBounds(bounds);
        }
        return null;
      },
      getGeofenceGroupsBounds(list = this.geofenceGroupFilters) {
        // Extend bounds by geofenceGroup bounds.
        if (list.length > 0) {
          const flattenedGeofenceGroupCoordinates = list
            .filter((gg) => gg.geofences.length > 0)
            .map((gg) =>
              gg.geofences
                .map((g) => [g.boundingBox.slice(0, 2), g.boundingBox.slice(2, 4)])
                .reduce((mem, x) => [...mem, ...x], []),
            )
            .reduce((mem, x) => [...mem, ...x], []);
          const bounds = getBounds(flattenedGeofenceGroupCoordinates);
          if (bounds != null) {
            return new mapboxgl.LngLatBounds(bounds);
          }
        }
        return null;
      },
      getLocationsBounds(list = this.locationFilters) {
        const flattenedMarketCoordinates = flattenGeoJsonGeometry(
          list
            .filter((x) => !this.cart.isExclusion(x))
            .reduce((mem, x) => [...mem, ...x.shapes.map((s) => s.geometry)], []),
        );
        const bounds = getBounds(flattenedMarketCoordinates);
        if (bounds != null) {
          return new mapboxgl.LngLatBounds(bounds);
        }
        return null;
      },
      async fitToBoundsOfAllLocations() {
        let bounds = null;

        const unitsAndSitesBounds = this.getUnitsAndSitesBounds();
        if (unitsAndSitesBounds != null) {
          if (bounds != null) {
            bounds.extend(unitsAndSitesBounds);
          } else {
            bounds = unitsAndSitesBounds;
          }
        }

        const marketFiltersBounds = this.getMarketsBounds();
        if (marketFiltersBounds != null) {
          if (bounds != null) {
            bounds.extend(marketFiltersBounds);
          } else {
            bounds = marketFiltersBounds;
          }
        }

        const geofenceFiltersBounds = this.getGeofenceGroupsBounds();
        if (geofenceFiltersBounds != null) {
          if (bounds != null) {
            bounds.extend(geofenceFiltersBounds);
          } else {
            bounds = geofenceFiltersBounds;
          }
        }

        const locationFiltersBounds = this.getLocationsBounds();
        if (locationFiltersBounds != null) {
          if (bounds != null) {
            bounds.extend(locationFiltersBounds);
          } else {
            bounds = locationFiltersBounds;
          }
        }

        if (bounds != null) {
          bounds = [
            [bounds.getSouthWest().lng, bounds.getSouthWest().lat],
            [bounds.getNorthEast().lng, bounds.getNorthEast().lat],
          ];
        }

        this.fitBounds(bounds);
      },
      fitBounds(bounds) {
        if (this.map == null) {
          return;
        }
        if (bounds != null) {
          this.lastFitBounds = bounds.map((x) => new this.BMap.Point(x[0], x[1]));
          this.map.setViewport(this.lastFitBounds);
        }
      },
      getOffsetAccordingToText(text) {
        const width = Array.from(text).reduce((mem, c) => mem + this.numberSizes[c].width, 0);
        return new this.BMap.Size(-width / 2, -11);
      },
      updateMarketOverlays() {
        if (this.marketOverlays != null) {
          this.marketOverlays.forEach((l) => {
            this.map.removeOverlay(l);
          });
        }
        this.marketOverlays = [];

        this.marketPolygonFeatures.forEach((market) => {
          market.geometry.coordinates.forEach((coordinates) => {
            const polygons = new this.BMap.Polygon(
              coordinates.map((pt) => new this.BMap.Point(pt[0], pt[1])),
              {
                strokeColor: Colors.success,
                fillColor: Colors.success,
                strokeWeight: 1,
                strokeOpacity: 0.75,
                fillOpacity: 0.2,
              },
            );
            this.map.addOverlay(polygons);
            this.marketOverlays.push(polygons);
          });
        });
      },
      updateLocationOverlays() {
        if (this.locationOverlays != null) {
          this.locationOverlays.forEach((l) => {
            this.map.removeOverlay(l);
          });
        }
        this.locationOverlays = [];

        this.locationPolygonFeatures.forEach((location) => {
          location.geometry.coordinates.forEach((coordinates) => {
            const polygons = new this.BMap.Polygon(
              coordinates.map((pt) => new this.BMap.Point(pt[0], pt[1])),
              {
                strokeColor: Colors.success,
                fillColor: Colors.success,
                strokeWeight: 1,
                strokeOpacity: 0.75,
                fillOpacity: 0.2,
              },
            );
            this.map.addOverlay(polygons);
            this.locationOverlays.push(polygons);
          });
        });
      },
      updateClusterMarkerOverlays() {
        if (this.BMap == null) {
          return;
        }

        if (this.clusterOverlays != null) {
          this.clusterOverlays.forEach((l) => {
            this.map.removeOverlay(l);
          });
        }
        this.clusterOverlays = [];

        const iconPin = new this.BMap.Icon('/assets/images/map/pin-x1.png', new this.BMap.Size(12, 12), {
          anchor: new this.BMap.Size(6, 6),
          infoWindowAnchor: new this.BMap.Size(6, 0),
        });

        const iconLg = new this.BMap.Icon('/assets/images/map/clusters/cluster-lg-x1.png', new this.BMap.Size(52, 52), {
          anchor: new this.BMap.Size(26, 26),
          infoWindowAnchor: new this.BMap.Size(26, 0),
        });

        const iconMd = new this.BMap.Icon('/assets/images/map/clusters/cluster-md-x1.png', new this.BMap.Size(47, 47), {
          anchor: new this.BMap.Size(23.5, 23.5),
          infoWindowAnchor: new this.BMap.Size(23.5, 0),
        });

        const iconSm = new this.BMap.Icon('/assets/images/map/clusters/cluster-sm-x1.png', new this.BMap.Size(42, 42), {
          anchor: new this.BMap.Size(21, 21),
          infoWindowAnchor: new this.BMap.Size(21, 0),
        });

        this.clusters.forEach((l) => {
          const point = new this.BMap.Point(l.lng, l.lat);

          let icon = null;
          if (l.count === 1) {
            icon = iconPin;
          } else if (l.count < 20) {
            icon = iconSm;
          } else if (l.count < 100) {
            icon = iconMd;
          } else {
            icon = iconLg;
          }

          const circle = new this.BMap.Marker(point, {
            icon,
          });
          circle.addEventListener('click', () => {
            this.onClusterClicked(l);
          });

          this.map.addOverlay(circle);
          this.clusterOverlays.push(circle);

          if (l.count > 1) {
            const label = new this.BMap.Label(l.countFormatted, {
              position: point,
              offset: this.getOffsetAccordingToText(l.countFormatted),
            });
            label.setStyle({
              color: 'white',
              fontSize: '15px',
              fontWeight: 'bold',
              fontFamily: 'Oxygen',
              borderWidth: 0,
              borderColor: '',
              backgroundColor: '',
              textAlign: 'center',
            });
            this.map.addOverlay(label);
            this.clusterOverlays.push(label);
          }
        });
      },
      showActiveFeatures() {
        // Called by OverallMap.vue, no need to implement for now.
      },
      onBoundsChanged(e) {
        if (this.isTriggeredFromCenterAndZoom) {
          this.isTriggeredFromCenterAndZoom = false;
        }
        if (e.target.getBounds().getSouthWest() != null) {
          this.bounds = [
            e.target.getBounds().getSouthWest().lng,
            e.target.getBounds().getSouthWest().lat,
            e.target.getBounds().getNorthEast().lng,
            e.target.getBounds().getNorthEast().lat,
          ];
        }
        this.zoom = e.target.getZoom();
        this.requestLocationsUpdate();
      },
      onClusterClicked(e) {
        const cluster = this.geocodedLocationsGeoJson.features.find(
          (x) => x.geometry.coordinates[0] === e.lng && x.geometry.coordinates[1] === e.lat,
        ); // this.clusters.find(c => c.lat === e.point.lat && c.lng === e.point.lng);
        if (cluster == null) {
          return;
        }
        this.fitBounds([
          [cluster.properties.bounds.min_lon, cluster.properties.bounds.min_lat],
          [cluster.properties.bounds.max_lon, cluster.properties.bounds.max_lat],
        ]);

        if (cluster.properties.unitIDs?.length <= 10 || cluster.properties.siteIDs?.length <= 10) {
          setTimeout(() => {
            const items = {
              type: cluster.properties?.unitIDs?.length ? MapInventoryType.UNITS : MapInventoryType.SITES,
              ids: cluster.properties?.unitIDs?.length ? cluster.properties.unitIDs : cluster.properties.siteIDs,
            };
            this.$emit('activeUnitIDsChanged', items);
          }, 10);
        }
      },
      onMapClicked() {
        this.$emit('activeUnitIDsChanged', []);
      },
      displayActiveSiteOrUnitOnMap() {
        // Called by OverallMap.vue, no need to implement for now.
      },
      onMapReady({ BMap, map }) {
        this.BMap = BMap;
        this.map = map;
        this.isMapLoaded = true;
        if (this.lastFitBounds != null) {
          this.fitBounds(this.lastFitBounds);
        } else {
          // Baidu map needs a center to load properly
          this.defaultZoom();
        }

        const debouncedRequestLocationsUpdate = debounce(this.onBoundsChanged.bind(this), 725, this.timeout);
        this.map.addEventListener('zoomend', debouncedRequestLocationsUpdate);
        this.map.addEventListener('touchend', debouncedRequestLocationsUpdate);
        this.map.addEventListener('dragend', debouncedRequestLocationsUpdate);
        this.map.addEventListener('resize', debouncedRequestLocationsUpdate);
      },
      enableMouseScrollZoom() {
        this.scrollWheelZoom = true;
      },
      disableMouseScrollZoom() {
        this.scrollWheelZoom = false;
      },
      resize() {
        // Called by OverallMap.vue, no need to implement for now.
      },
      defaultZoom() {
        this.isTriggeredFromCenterAndZoom = true;
        this.map?.centerAndZoom('中国');
      },
    },
  };
</script>

<style scoped>
  .baidu-map-container {
    width: 100%;
    height: 100%;
    background: #fff;
  }

  .map {
    width: 100%;
    height: 100%;
  }

  .loader-container {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
